import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// Customer-facing reads. All queries flow through the RLS-scoped client.
function asAny<T>(v: T) {
  return v as unknown as {
    from: (t: string) => any;
    storage: any;
    rpc: (n: string, a?: any) => Promise<{ data: any; error: { message: string } | null }>;
  };
}

async function requireMyProject(supabase: any, userId: string, projectId: string) {
  const sb = asAny(supabase);
  const { data, error } = await sb
    .from("customer_projects")
    .select("id, customer_user_id, customer_name, customer_email, proposal_id, project_name")
    .eq("id", projectId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data || data.customer_user_id !== userId) throw new Error("forbidden");
  return data as { id: string; customer_user_id: string; customer_name: string | null; customer_email: string; proposal_id: string | null; project_name: string | null };
}

export const getMyProject = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = asAny(context.supabase);
    const { data: project, error } = await sb
      .from("customer_projects")
      .select("*")
      .eq("customer_user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!project) return { project: null as any, timeline: [], nextAppointment: null, unreadMessages: 0, payments: [] };

    const [{ data: timeline }, { data: nextAppt }, { count: unread }, { data: payments }] = await Promise.all([
      sb.from("project_timeline").select("id, stage, status, occurred_on, est_completion, stage_order, notes")
        .eq("project_id", project.id).order("stage_order", { ascending: true }),
      sb.from("project_appointments").select("id, kind, title, starts_at, status, location")
        .eq("project_id", project.id).gte("starts_at", new Date().toISOString())
        .order("starts_at", { ascending: true }).limit(1).maybeSingle(),
      sb.from("project_messages").select("id", { count: "exact", head: true })
        .eq("project_id", project.id).neq("sender_user_id", context.userId),
      sb.from("payment_records").select("amount, kind, status").eq("project_id", project.id),
    ]);

    return {
      project,
      timeline: timeline ?? [],
      nextAppointment: nextAppt ?? null,
      unreadMessages: (unread as number | null) ?? 0,
      payments: payments ?? [],
    };
  });

export const listMyNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = asAny(context.supabase);
    const { data, error } = await sb
      .from("project_notifications")
      .select("*")
      .eq("user_id", context.userId)
      .is("archived_at", null)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (data ?? []) as Array<{ id: string; title: string; body: string | null; created_at: string; read_at: string | null; kind: string }>;
  });

const ClaimSchema = z.object({ token: z.string().min(8) });

export const claimInvitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ClaimSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { data: result, error } = await sb.rpc("claim_customer_invitation", { _token: data.token });
    if (error) throw new Error(error.message);
    return result as { ok: boolean; project_id: string };
  });

// ---------------------------------------------------------------------------
//  PROPOSAL
// ---------------------------------------------------------------------------

const ProjectIdSchema = z.object({ projectId: z.string().uuid() });

export const getMyProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { data: result, error } = await sb.rpc("cp_get_my_proposal", { _project_id: data.projectId });
    if (error) throw new Error(error.message);
    return result as null | { proposal: any; line_items: any[]; colors: any[] };
  });

const DecisionSchema = z.object({
  projectId: z.string().uuid(),
  decision: z.enum(["approve", "decline"]),
  name: z.string().trim().max(120).optional(),
  reason: z.string().trim().max(2000).optional(),
  signature: z.string().max(500000).optional(),
});

export const decideMyProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DecisionSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { data: result, error } = await sb.rpc("cp_proposal_decision", {
      _project_id: data.projectId,
      _decision: data.decision,
      _name: data.name ?? "",
      _reason: data.reason ?? "",
      _signature: data.signature ?? null,
    });
    if (error) throw new Error(error.message);
    // Move the linked opportunity to Won or Lost (fail-soft).
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: proj } = await supabaseAdmin
        .from("customer_projects")
        .select("proposal_id")
        .eq("id", data.projectId)
        .maybeSingle();
      if (proj?.proposal_id) {
        const { markOpportunityFromProposalDecision } = await import("@/lib/crm/opportunities.server");
        await markOpportunityFromProposalDecision(supabaseAdmin, proj.proposal_id, data.decision, data.reason);
      }
    } catch (e) { console.warn("[opportunity decision sync portal]", e); }
    return result as { ok: boolean; status: string };
  });

const ChangeRequestSchema = z.object({
  projectId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  message: z.string().trim().min(3).max(4000),
});

export const requestMyProposalChanges = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ChangeRequestSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { data: result, error } = await sb.rpc("cp_proposal_request_changes", {
      _project_id: data.projectId,
      _name: data.name,
      _email: data.email,
      _message: data.message,
    });
    if (error) throw new Error(error.message);
    return result as { ok: boolean };
  });

export const generateMyProposalPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const proj = await requireMyProject(context.supabase, context.userId, data.projectId);
    if (!proj.proposal_id) throw new Error("no_proposal");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [p, items, colors, settings] = await Promise.all([
      supabaseAdmin.from("proposals").select("*").eq("id", proj.proposal_id).maybeSingle(),
      supabaseAdmin.from("proposal_line_items").select("*").eq("proposal_id", proj.proposal_id).order("sort_order"),
      supabaseAdmin.from("proposal_colors").select("*").eq("proposal_id", proj.proposal_id).order("sort_order"),
      supabaseAdmin.from("admin_settings").select("business_name, business_email, business_phone, company_website, company_logo_url").eq("id", 1).maybeSingle(),
    ]);
    if (p.error || !p.data) throw new Error(p.error?.message || "not_found");
    const company = settings.data ? {
      name: settings.data.business_name ?? "Michael's Painting",
      email: settings.data.business_email,
      phone: settings.data.business_phone,
      website: settings.data.company_website,
      logo_url: settings.data.company_logo_url,
    } : {};
    const { renderProposalPdf } = await import("@/lib/proposals-pdf.server");
    const bytes = await renderProposalPdf({
      proposal: p.data,
      line_items: items.data ?? [],
      colors: colors.data ?? [],
      company,
    });
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return {
      filename: `Proposal-${p.data.proposal_number || p.data.id}.pdf`,
      mimeType: "application/pdf",
      base64: btoa(binary),
    };
  });

// ---------------------------------------------------------------------------
//  COLORS
// ---------------------------------------------------------------------------

export const listMyColors = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    // Resolve proposal -> proposal_colors via secure RPC (so we don't depend on a customer RLS read of proposals).
    const { data: result } = await sb.rpc("cp_get_my_proposal", { _project_id: data.projectId });
    if (!result) return { colors: [], beforeImage: null, designImage: null, renderImage: null };
    const prop = result.proposal as any;
    return {
      colors: (result.colors ?? []) as Array<{ id: string; surface: string; color_name: string; color_number: string | null; hex: string | null; lrv: number | null; sheen: string | null; product_line: string | null; coats: number | null }>,
      beforeImage: prop?.cover_image_url ?? null,
      designImage: prop?.design_image_url ?? null,
      renderImage: prop?.studio_render_url ?? null,
    };
  });

// ---------------------------------------------------------------------------
//  GALLERY
// ---------------------------------------------------------------------------

const ALBUMS = ["inspiration", "before", "progress", "after"] as const;

export const listMyGallery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    await requireMyProject(context.supabase, context.userId, data.projectId);
    const sb = asAny(context.supabase);
    const { data: photos, error } = await sb
      .from("project_photos")
      .select("id, album, url, thumb_url, caption, uploaded_by_role, created_at")
      .eq("project_id", data.projectId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (photos ?? []) as Array<{
      id: string; album: typeof ALBUMS[number]; url: string;
      thumb_url: string | null; caption: string | null;
      uploaded_by_role: string | null; created_at: string;
    }>;
  });

const AddPhotoSchema = z.object({
  projectId: z.string().uuid(),
  album: z.enum(ALBUMS),
  url: z.string().url(),
  caption: z.string().trim().max(280).optional(),
});

export const addMyGalleryPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => AddPhotoSchema.parse(d))
  .handler(async ({ context, data }) => {
    // Customers can only upload to the "inspiration" album.
    if (data.album !== "inspiration") throw new Error("forbidden_album");
    const sb = asAny(context.supabase);
    const { error } = await sb.from("project_photos").insert({
      project_id: data.projectId,
      album: data.album,
      url: data.url,
      caption: data.caption ?? null,
      uploaded_by: context.userId,
      uploaded_by_role: "customer",
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
//  DOCUMENTS
// ---------------------------------------------------------------------------

export const listMyDocuments = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    await requireMyProject(context.supabase, context.userId, data.projectId);
    const sb = asAny(context.supabase);
    const { data: docs, error } = await sb
      .from("project_documents")
      .select("id, kind, title, file_url, file_size_bytes, mime_type, created_at")
      .eq("project_id", data.projectId)
      .eq("visible_to_customer", true)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (docs ?? []) as Array<{ id: string; kind: string; title: string; file_url: string; file_size_bytes: number | null; mime_type: string | null; created_at: string }>;
  });

const SignedUrlSchema = z.object({
  projectId: z.string().uuid(),
  path: z.string().min(1).max(500),
});

export const getMyFileSignedUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SignedUrlSchema.parse(d))
  .handler(async ({ context, data }) => {
    await requireMyProject(context.supabase, context.userId, data.projectId);
    // Path must live under the project's prefix.
    if (!data.path.startsWith(`${data.projectId}/`)) throw new Error("forbidden_path");
    const sb = asAny(context.supabase);
    const { data: signed, error } = await sb.storage.from("project-files").createSignedUrl(data.path, 300);
    if (error) throw new Error(error.message);
    return { url: signed.signedUrl as string };
  });

// ---------------------------------------------------------------------------
//  MESSAGES
// ---------------------------------------------------------------------------

export const listMyMessages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    await requireMyProject(context.supabase, context.userId, data.projectId);
    const sb = asAny(context.supabase);
    const { data: rows, error } = await sb
      .from("project_messages")
      .select("id, sender_user_id, sender_name, sender_role, body, attachments, created_at, read_by")
      .eq("project_id", data.projectId)
      .order("created_at", { ascending: true })
      .limit(500);
    if (error) throw new Error(error.message);
    return {
      messages: (rows ?? []) as Array<{ id: string; sender_user_id: string | null; sender_name: string | null; sender_role: string | null; body: string | null; attachments: any; created_at: string; read_by: any }>,
      me: context.userId,
    };
  });

const SendMessageSchema = z.object({
  projectId: z.string().uuid(),
  body: z.string().trim().max(4000).optional(),
  attachments: z.array(z.object({ url: z.string().url(), name: z.string().max(200), mime: z.string().max(100).optional() })).max(5).optional(),
});

export const sendMyMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SendMessageSchema.parse(d))
  .handler(async ({ context, data }) => {
    if (!data.body && !data.attachments?.length) throw new Error("empty_message");
    const proj = await requireMyProject(context.supabase, context.userId, data.projectId);
    const sb = asAny(context.supabase);
    const senderName = proj.customer_name || proj.customer_email;
    const { error } = await sb.from("project_messages").insert({
      project_id: data.projectId,
      sender_user_id: context.userId,
      sender_name: senderName,
      sender_role: "customer",
      body: data.body ?? null,
      attachments: data.attachments ?? [],
      read_by: [context.userId],
    });
    if (error) throw new Error(error.message);

    // Fire-and-forget email notification to the admin team.
    void notifyAdminOfMessage({
      projectName: proj.project_name || "your project",
      senderName: senderName || "Customer",
      body: data.body ?? "(attachment)",
    });
    // Push to admins.
    try {
      const { sendPushToRole } = await import("@/lib/push-sender.server");
      await sendPushToRole("admin", "admin", "messages", {
        title: `New message from ${senderName || "customer"}`,
        body: (data.body ?? "(attachment)").slice(0, 140),
        url: `/admin/customers/${data.projectId}?tab=messages`,
        tag: `msg-${data.projectId}`,
      });
    } catch (e) {
      console.warn("[push] admin message push failed:", (e as Error).message);
    }
    return { ok: true };
  });

export const markMyMessagesRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { error } = await sb.rpc("cp_mark_messages_read", { _project_id: data.projectId });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

async function notifyAdminOfMessage(args: { projectName: string; senderName: string; body: string }) {
  const apiKey = process.env.RESEND_API_KEY;
  const to = process.env.RESEND_ADMIN_TO;
  if (!apiKey || !to) return;
  const from = process.env.RESEND_FROM || "Michael's Painting <notifications@michaelspaintingwi.com>";
  const html = `<!doctype html><html><body style="margin:0;background:#0a0a0a">
    <div style="font-family:Inter,Arial,sans-serif;background:#0a0a0a;padding:32px;color:#e5e5e5">
      <div style="max-width:560px;margin:0 auto;background:#111;border:1px solid #262626;border-radius:18px;padding:28px">
        <p style="color:#fbbf24;font-size:11px;letter-spacing:.25em;text-transform:uppercase;margin:0">New portal message</p>
        <h1 style="font-family:Georgia,serif;color:#fff;font-size:22px;margin:8px 0 14px">${escapeHtml(args.senderName)} — ${escapeHtml(args.projectName)}</h1>
        <p style="color:#d4d4d8;line-height:1.6;white-space:pre-wrap;margin:0">${escapeHtml(args.body)}</p>
      </div>
    </div></body></html>`;
  try {
    const res = await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({ from, to: [to], subject: `New portal message — ${args.projectName}`, html }),
    });
    if (!res.ok) {
      const { alertResendFailure } = await import("@/lib/logging/integration-failure.server");
      await alertResendFailure({ res, subject: "portal message notify", route: "portal/portal.functions:notifyAdminNewMessage" });
    }
  } catch (e) {
    console.error("[portal message email]", e);
    const { alertResendException } = await import("@/lib/logging/integration-failure.server");
    await alertResendException({ error: e, subject: "portal message notify", route: "portal/portal.functions:notifyAdminNewMessage" });
  }
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

// ---------------------------------------------------------------------------
//  SCHEDULE
// ---------------------------------------------------------------------------

export const listMyAppointments = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    await requireMyProject(context.supabase, context.userId, data.projectId);
    const sb = asAny(context.supabase);
    const { data: rows, error } = await sb
      .from("project_appointments")
      .select("id, kind, title, starts_at, ends_at, status, location, notes, customer_response, updated_at")
      .eq("project_id", data.projectId)
      .order("starts_at", { ascending: true });
    if (error) throw new Error(error.message);
    return (rows ?? []) as Array<{
      id: string; kind: string; title: string; starts_at: string; ends_at: string | null;
      status: string; location: string | null; notes: string | null;
      customer_response: string | null; updated_at: string;
    }>;
  });

const RescheduleSchema = z.object({
  appointmentId: z.string().uuid(),
  proposedAt: z.string().datetime(),
  note: z.string().trim().max(500).optional(),
});

export const requestMyReschedule = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => RescheduleSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { error } = await sb.rpc("cp_request_reschedule", {
      _appointment_id: data.appointmentId,
      _proposed_at: data.proposedAt,
      _note: data.note ?? "",
    });
    if (error) throw new Error(error.message);
    void notifyAdminGeneric("Reschedule requested",
      `A customer requested a new time (${new Date(data.proposedAt).toLocaleString()}): ${data.note ?? ""}`);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
//  PAYMENTS
// ---------------------------------------------------------------------------

export const getMyPaymentSummary = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const [projectRes, paymentsRes] = await Promise.all([
      sb.from("customer_projects")
        .select("total_amount, deposit_amount, amount_paid, balance_due")
        .eq("id", data.projectId).maybeSingle(),
      sb.from("payment_records")
        .select("id, kind, amount, method, provider, reference, status, recorded_at, notes")
        .eq("project_id", data.projectId)
        .order("recorded_at", { ascending: false }),
    ]);
    if (projectRes.error) throw new Error(projectRes.error.message);
    if (paymentsRes.error) throw new Error(paymentsRes.error.message);

    const records = (paymentsRes.data ?? []) as Array<{
      id: string; kind: string; amount: number; method: string; provider: string;
      reference: string | null; status: string; recorded_at: string; notes: string | null;
    }>;
    const project = projectRes.data ?? { total_amount: 0, deposit_amount: 0, amount_paid: 0, balance_due: 0 };
    return {
      totals: {
        total: Number(project.total_amount ?? 0),
        deposit: Number(project.deposit_amount ?? 0),
        paid: Number(project.amount_paid ?? 0),
        balance: Number(project.balance_due ?? Math.max(0, Number(project.total_amount ?? 0) - Number(project.amount_paid ?? 0))),
      },
      records,
      provider: { configured: false, name: "Payment provider not configured" },
    };
  });

// ---------------------------------------------------------------------------
//  WARRANTY
// ---------------------------------------------------------------------------

export const getMyWarranty = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProjectIdSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const [{ data: warranty }, { data: claims }] = await Promise.all([
      sb.from("customer_warranty")
        .select("id, coverage_years, starts_on, expires_on, document_url, details")
        .eq("project_id", data.projectId).maybeSingle(),
      sb.from("warranty_claims")
        .select("id, description, photos, status, admin_notes, created_at, updated_at")
        .eq("project_id", data.projectId).order("created_at", { ascending: false }),
    ]);
    return {
      warranty: warranty ?? null,
      claims: (claims ?? []) as Array<{
        id: string; description: string; photos: any; status: string;
        admin_notes: string | null; created_at: string; updated_at: string;
      }>,
    };
  });

const ClaimSubmitSchema = z.object({
  projectId: z.string().uuid(),
  description: z.string().trim().min(5).max(4000),
  photos: z.array(z.string().min(1).max(500)).max(10).optional(),
});

export const submitMyWarrantyClaim = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ClaimSubmitSchema.parse(d))
  .handler(async ({ context, data }) => {
    const proj = await requireMyProject(context.supabase, context.userId, data.projectId);
    // Photos must live under the project's storage prefix
    for (const p of data.photos ?? []) {
      if (!p.startsWith(`${data.projectId}/`)) throw new Error("forbidden_path");
    }
    const sb = asAny(context.supabase);
    const { error } = await sb.from("warranty_claims").insert({
      project_id: data.projectId,
      submitted_by: context.userId,
      description: data.description,
      photos: data.photos ?? [],
      status: "submitted",
    });
    if (error) throw new Error(error.message);
    void notifyAdminGeneric("New warranty request",
      `${proj.customer_name || proj.customer_email} submitted a warranty request:\n\n${data.description}`);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
//  PROFILE
// ---------------------------------------------------------------------------

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = asAny(context.supabase);
    const [{ data: prefs }, { data: project }] = await Promise.all([
      sb.from("customer_preferences")
        .select("display_name, phone, comm_email, comm_sms, comm_push, secondary_contact, addresses")
        .eq("user_id", context.userId).maybeSingle(),
      sb.from("customer_projects")
        .select("customer_name, customer_email, customer_phone, address, city, state, zip, project_name")
        .eq("customer_user_id", context.userId).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    ]);
    return {
      email: (context.claims as any)?.email ?? project?.customer_email ?? null,
      preferences: prefs ?? {
        display_name: project?.customer_name ?? "",
        phone: project?.customer_phone ?? "",
        comm_email: true, comm_sms: false, comm_push: true,
        secondary_contact: {}, addresses: [],
      },
      property: project ? {
        address: project.address, city: project.city, state: project.state, zip: project.zip,
        project_name: project.project_name,
      } : null,
    };
  });

const ProfileUpdateSchema = z.object({
  display_name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(40).optional(),
  comm_email: z.boolean(),
  comm_sms: z.boolean(),
  comm_push: z.boolean(),
  secondary_contact: z.object({
    name: z.string().trim().max(120).optional(),
    phone: z.string().trim().max(40).optional(),
    email: z.string().trim().email().optional().or(z.literal("")),
    relationship: z.string().trim().max(60).optional(),
  }).default({}),
});

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ProfileUpdateSchema.parse(d))
  .handler(async ({ context, data }) => {
    const sb = asAny(context.supabase);
    const { error } = await sb.rpc("cp_update_my_profile", {
      _display_name: data.display_name,
      _phone: data.phone ?? null,
      _comm_email: data.comm_email,
      _comm_sms: data.comm_sms,
      _comm_push: data.comm_push,
      _secondary_contact: data.secondary_contact,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

async function notifyAdminGeneric(subject: string, body: string) {
  const apiKey = process.env.RESEND_API_KEY;
  const to = process.env.RESEND_ADMIN_TO;
  if (!apiKey || !to) return;
  const from = process.env.RESEND_FROM || "Michael's Painting <notifications@michaelspaintingwi.com>";
  const html = `<!doctype html><html><body style="font-family:Inter,Arial,sans-serif;background:#0a0a0a;padding:32px;color:#e5e5e5">
    <div style="max-width:560px;margin:0 auto;background:#111;border:1px solid #262626;border-radius:18px;padding:28px">
      <p style="color:#fbbf24;font-size:11px;letter-spacing:.25em;text-transform:uppercase;margin:0">${escapeHtml(subject)}</p>
      <p style="color:#d4d4d8;white-space:pre-wrap;line-height:1.6;margin:14px 0 0">${escapeHtml(body)}</p>
    </div></body></html>`;
  try {
    const res = await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({ from, to: [to], subject, html }),
    });
    if (!res.ok) {
      const { alertResendFailure } = await import("@/lib/logging/integration-failure.server");
      await alertResendFailure({ res, subject, route: "portal/portal.functions:notifyAdminGeneric" });
    }
  } catch (e) {
    console.error("[portal notify]", e);
    const { alertResendException } = await import("@/lib/logging/integration-failure.server");
    await alertResendException({ error: e, subject, route: "portal/portal.functions:notifyAdminGeneric" });
  }
}